import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expertDashboardApi } from '../../../lib/http';
import type { BookingDetail } from '../../../types';
import toast from 'react-hot-toast';
import { BookingStatusBadge } from './BookingStatusBadge';
import { BookingServiceInfo } from './BookingServiceInfo';
import { BookingClientInfo } from './BookingClientInfo';
import { BookingNotes } from './BookingNotes';
import { BookingClientHistory } from './BookingClientHistory';
import { BookingActions } from './BookingActions';
import { BookingCancelDialog } from './BookingCancelDialog';

interface BookingDetailModalProps {
  bookingId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onBookingUpdated?: (booking: BookingDetail) => void;
}

export const BookingDetailModal: React.FC<BookingDetailModalProps> = ({
  bookingId,
  isOpen,
  onClose,
  onBookingUpdated,
}) => {
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Fetch booking details
  const { data: bookingResponse, isLoading, error } = useQuery<{ booking: BookingDetail }>({
    queryKey: ['booking', bookingId],
    queryFn: async () => {
      if (!bookingId) throw new Error('No booking ID');
      const response = await expertDashboardApi.bookings.get(bookingId);
      return response.data;
    },
    enabled: isOpen && !!bookingId,
  });

  const booking = bookingResponse?.booking;

  // Confirm mutation
  const confirmMutation = useMutation({
    mutationFn: () => {
      if (!bookingId) throw new Error('No booking ID');
      return expertDashboardApi.bookings.confirm(bookingId);
    },
    onSuccess: (response) => {
      toast.success('Booking confirmed!');
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
      if (response.data?.booking) {
        onBookingUpdated?.(response.data.booking);
      }
      // Auto-close after 1.5s
      setTimeout(() => handleClose(), 1500);
    },
    onError: () => {
      toast.error('Failed to confirm booking. Please try again.');
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => {
      if (!bookingId) throw new Error('No booking ID');
      return expertDashboardApi.bookings.cancel(bookingId);
    },
    onSuccess: (response) => {
      toast.success('Booking cancelled');
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
      if (response.data?.booking) {
        onBookingUpdated?.(response.data.booking);
      }
      setShowCancelDialog(false);
      // Auto-close after 1.5s
      setTimeout(() => handleClose(), 1500);
    },
    onError: () => {
      toast.error('Failed to cancel booking. Please try again.');
      setShowCancelDialog(false);
    },
  });

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !showCancelDialog) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showCancelDialog]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setShowCancelDialog(false);
    }, 300);
  };

  const handleConfirm = () => {
    confirmMutation.mutate();
  };

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = () => {
    cancelMutation.mutate();
  };

  const handleCancelGoBack = () => {
    setShowCancelDialog(false);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !showCancelDialog) {
      handleClose();
    }
  };

  const handleBookingClick = (clickedBookingId: string) => {
    // Close current modal and potentially open the clicked booking
    // For now, just close as we'd need to pass this up to parent
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
          isClosing ? 'opacity-0' : 'opacity-60'
        } md:opacity-40`}
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 z-50 flex items-end md:items-center md:justify-center transition-all duration-300 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleBackdropClick}
      >
        <div
          className={`bg-white w-full h-full md:h-auto md:max-h-[90vh] md:w-[480px] md:rounded-2xl flex flex-col transform transition-transform duration-300 shadow-xl ${
            isClosing ? 'translate-y-full md:translate-y-0 md:scale-95' : 'translate-y-0 md:scale-100'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-4 md:rounded-t-2xl relative">
            <button
              type="button"
              onClick={() => handleClose()}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-colors cursor-pointer z-10"
              aria-label="Close booking details"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-center text-lg font-semibold text-gray-900">
              Booking Details
            </h2>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-3" />
                  <p className="text-sm text-gray-600">Loading booking details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12 px-4">
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-red-400 mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="text-base font-medium text-gray-900 mb-2">Booking Not Found</p>
                  <p className="text-sm text-gray-600 mb-4">
                    This booking may have been deleted or is no longer available.
                  </p>
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Return to Calendar
                  </button>
                </div>
              </div>
            ) : booking ? (
              <>
                {/* Status Badge */}
                <BookingStatusBadge status={booking.status} />

                {/* Service Information */}
                <BookingServiceInfo
                  service={booking.service}
                  dateFormatted={booking.date_formatted}
                  timeRange={booking.time_range}
                />

                {/* Client Information */}
                <BookingClientInfo client={booking.client} />

                {/* Booking Notes */}
                {booking.notes && <BookingNotes notes={booking.notes} />}

                {/* Client History */}
                {booking.client_history && (
                  <BookingClientHistory
                    clientHistory={booking.client_history}
                    onBookingClick={handleBookingClick}
                  />
                )}

                {/* Payment Info (if exists) */}
                {booking.payment && (
                  <div className="mx-4 mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-green-700 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Payment
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {booking.payment.status.toUpperCase()}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Paid {new Date(booking.payment.paid_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-lg font-bold text-green-600">
                        {booking.payment.amount_formatted}
                      </div>
                    </div>
                  </div>
                )}

                {/* Add spacing for sticky footer */}
                <div className="h-4" />
              </>
            ) : null}
          </div>

          {/* Sticky Footer with Actions */}
          {booking && (
            <BookingActions
              status={booking.status}
              isConfirming={confirmMutation.isPending}
              isCancelling={cancelMutation.isPending}
              onConfirm={handleConfirm}
              onCancelClick={handleCancelClick}
            />
          )}
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      {booking && (
        <BookingCancelDialog
          isOpen={showCancelDialog}
          booking={booking}
          isCancelling={cancelMutation.isPending}
          onCancel={handleCancelConfirm}
          onGoBack={handleCancelGoBack}
        />
      )}
    </>
  );
};
